import React from "react";
import "../styles/panel-sidebar.css";

export interface PanelSidebarProps {
    open?: boolean;
    width?: number;
    className?: string;
    ariaLabel?: string;
    children: React.ReactNode;
}

function PanelSidebarRoot({
    open = true,
    width = 260,
    className,
    ariaLabel,
    children,
}: PanelSidebarProps) {
    return (
        <aside
            className={["panel-sidebar", open ? "open" : "closed", className].filter(Boolean).join(" ")}
            style={{ "--panel-sidebar-w": `${width}px` } as React.CSSProperties}
            aria-label={ariaLabel}
        >
            {children}
        </aside>
    );
}

function PanelSidebarHeader({
    title,
    children,
}: {
    title?: string;
    children?: React.ReactNode;
}) {
    return (
        <div className="panel-sidebar__header">
            {title && <h2 className="panel-sidebar__title">{title}</h2>}
            {children}
        </div>
    );
}

function PanelSidebarAction({
    onClick,
    children,
    icon,
}: {
    onClick: () => void;
    children: React.ReactNode;
    icon?: React.ReactNode;
}) {
    return (
        <button type="button" className="panel-sidebar__action" onClick={onClick}>
            {icon}
            {children}
        </button>
    );
}

function PanelSidebarBody({ children }: { children: React.ReactNode }) {
    return <div className="panel-sidebar__body">{children}</div>;
}

function PanelSidebarEmpty({ children }: { children: React.ReactNode }) {
    return <div className="panel-sidebar__empty">{children}</div>;
}

function PanelSidebarGroup({
    label,
    children,
}: {
    label: string;
    children: React.ReactNode;
}) {
    return (
        <div className="panel-sidebar__group">
            <div className="panel-sidebar__group-label">{label}</div>
            {children}
        </div>
    );
}

function PanelSidebarItem({
    active = false,
    onClick,
    onDelete,
    icon,
    children,
    deleteLabel = "Remove",
}: {
    active?: boolean;
    onClick: () => void;
    onDelete?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    icon?: React.ReactNode;
    children: React.ReactNode;
    deleteLabel?: string;
}) {
    return (
        <div
            className={`panel-sidebar__item-row${active ? " is-active" : ""}`}
            role="listitem"
        >
            <button type="button" className="panel-sidebar__item" onClick={onClick}>
                {icon && <span className="panel-sidebar__item-icon">{icon}</span>}
                <span className="panel-sidebar__item-label">{children}</span>
            </button>
            {onDelete && (
                <button
                    type="button"
                    className="panel-sidebar__item-delete"
                    onClick={onDelete}
                    title={deleteLabel}
                    aria-label={deleteLabel}
                >
                    <TrashIcon />
                </button>
            )}
        </div>
    );
}

const TrashIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
    </svg>
);

export const PanelSidebar = Object.assign(PanelSidebarRoot, {
    Header: PanelSidebarHeader,
    Action: PanelSidebarAction,
    Body: PanelSidebarBody,
    Empty: PanelSidebarEmpty,
    Group: PanelSidebarGroup,
    Item: PanelSidebarItem,
});

export default PanelSidebar;
